// Server-trusted canonical price catalog for checkout order creation.
// This is the single source of truth for pack pricing across server + client.
// Prices are in INR rupees.

export type SkuHandle = "mag-absorb-pro" | "liver-biome-plus" | "ova-balance";
export type PackQty = 1 | 2 | 3;

export type PackPricing = { price: number; mrp: number };

// Canonical catalog: includes MRP for client-side display of savings.
// Real per-unit ladder discount vs. buying the 30-day pack repeatedly:
//   60-day ≈ 8% off, 90-day ≈ 18% off. MRP is untouched, so the displayed
//   "Save ₹X" badges (mrp − price) stay accurate against MRP.
export const PACK_PRICING: Record<SkuHandle, Record<PackQty, PackPricing>> = {
  "mag-absorb-pro": {
    1: { price: 1359, mrp: 1799 },
    2: { price: 2579, mrp: 3598 },
    3: { price: 3439, mrp: 5397 },
  },
  "liver-biome-plus": {
    1: { price: 1719, mrp: 2199 },
    2: { price: 3129, mrp: 4398 },
    3: { price: 4179, mrp: 6597 },
  },
  "ova-balance": {
    1: { price: 1899, mrp: 2399 },
    2: { price: 3493, mrp: 4798 }, // 2×1899 = 3798 → 8.0% off
    3: { price: 4675, mrp: 7197 }, // 3×1899 = 5697 → 17.9% off
  },
};

/**
 * Best-effort SKU inference from a cart line's handle/title. Server checkout
 * paths often only know the display title, so title matching must work.
 * Shared by bundle/tiered pricing and the inaugural campaign resolver.
 */
export function inferSkuHandle(input: {
  handle?: string | null;
  title?: string | null;
}): SkuHandle | null {
  if (input.handle && PACK_PRICING[input.handle as SkuHandle]) {
    return input.handle as SkuHandle;
  }
  const s = `${input.title ?? ""} ${input.handle ?? ""}`.toLowerCase();
  if (s.includes("mag") || s.includes("absorb")) return "mag-absorb-pro";
  if (s.includes("liver") || s.includes("biome")) return "liver-biome-plus";
  if (s.includes("ova")) return "ova-balance";
  return null;
}


/**
 * Runtime overrides published from Admin → Products → Product page editor.
 * Hydrated on the client from the product page loader and on the server
 * before any discount/checkout maths, so displayed and charged prices match.
 */
const RUNTIME_PACK_PRICING: Partial<
  Record<SkuHandle, Partial<Record<PackQty, PackPricing>>>
> = {};

export function clearRuntimePackPricing(): void {
  for (const handle of Object.keys(RUNTIME_PACK_PRICING)) delete RUNTIME_PACK_PRICING[handle as SkuHandle];
}

export function setRuntimePackPricing(
  handle: string,
  table: Partial<Record<PackQty, PackPricing>>,
): void {
  if (!PACK_PRICING[handle as SkuHandle]) return;
  RUNTIME_PACK_PRICING[handle as SkuHandle] = table;
}

/** Effective pack pricing table for a SKU (code defaults + published overrides). */
export function getPackPricingTable(
  handle: string,
): Record<PackQty, PackPricing> | undefined {
  const base = PACK_PRICING[handle as SkuHandle];
  if (!base) return undefined;
  const ov = RUNTIME_PACK_PRICING[handle as SkuHandle];
  if (!ov) return base;
  return {
    1: ov[1] ?? base[1],
    2: ov[2] ?? base[2],
    3: ov[3] ?? base[3],
  };
}

export function getPackPricing(handle: string, qty: PackQty): PackPricing | undefined {
  return getPackPricingTable(handle)?.[qty];
}

// Server-side view: price only (checkout doesn't need MRP).
export const PRICE_CATALOG: Record<SkuHandle, Record<PackQty, number>> = {
  "mag-absorb-pro": {
    1: PACK_PRICING["mag-absorb-pro"][1].price,
    2: PACK_PRICING["mag-absorb-pro"][2].price,
    3: PACK_PRICING["mag-absorb-pro"][3].price,
  },
  "liver-biome-plus": {
    1: PACK_PRICING["liver-biome-plus"][1].price,
    2: PACK_PRICING["liver-biome-plus"][2].price,
    3: PACK_PRICING["liver-biome-plus"][3].price,
  },
  "ova-balance": {
    1: PACK_PRICING["ova-balance"][1].price,
    2: PACK_PRICING["ova-balance"][2].price,
    3: PACK_PRICING["ova-balance"][3].price,
  },
};

/**
 * Free shipping applies to every order — no minimum order value.
 */
export const FREE_SHIPPING_THRESHOLD_INR = 0;
/** Minimum order value for no-cost EMI on the payment gateway (rupees). */
export const NO_COST_EMI_MIN_INR = 3000;
export const SHIPPING_FEE_INR = 79;
/** Partial-COD advance (rupees) paid online; deducted from the order total, balance collected on delivery. No COD fee. */
export const COD_ADVANCE_INR = 99;
export const PREPAID_DISCOUNT_RATE = 0.05;

export type IncomingLine = { handle: string; qty: number };

export type PricedCart = {
  lines: Array<{ handle: SkuHandle; qty: PackQty; unitPrice: number; lineTotal: number }>;
  subtotal: number;
  shipping: number;
  prepaidDiscount: number;
  total: number; // INR rupees
};

export function priceCart(input: IncomingLine[], prepaid: boolean): PricedCart {
  if (!Array.isArray(input) || input.length === 0) throw new Error("Cart is empty");
  const lines = input.map((l) => {
    const handle = l.handle as SkuHandle;
    if (!PRICE_CATALOG[handle]) throw new Error(`Unknown SKU: ${l.handle}`);
    const qty = Number(l.qty);
    if (![1, 2, 3].includes(qty)) throw new Error(`Invalid qty for ${l.handle}: ${l.qty}`);
    const unitPrice = PRICE_CATALOG[handle][qty as PackQty];
    return { handle, qty: qty as PackQty, unitPrice, lineTotal: unitPrice };
  });
  const subtotal = lines.reduce((s, l) => s + l.lineTotal, 0);
  const shipping = subtotal >= FREE_SHIPPING_THRESHOLD_INR ? 0 : SHIPPING_FEE_INR;
  const prepaidDiscount = prepaid ? Math.round(subtotal * PREPAID_DISCOUNT_RATE) : 0;
  const total = Math.max(0, subtotal + shipping - prepaidDiscount);
  return { lines, subtotal, shipping, prepaidDiscount, total };
}

